// services/authService.js - ИСПРАВЛЕННАЯ ВЕРСИЯ
// Адаптирована под существующий backend API (passwordRecovery, verifyResetToken, resetPassword)

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class AuthService {
    constructor() {
        this.token = localStorage.getItem('auth_token');
        this.isOnline = navigator.onLine;
        this.retryCount = 0;
        this.maxRetries = 3;

        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('Network: back online');
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('Network: gone offline');
        });
    }

    getHeaders(isFormData = false) {
        const headers = {};

        if (!isFormData) {
            headers['Content-Type'] = 'application/json';
        }

        headers['Accept'] = 'application/json';
        headers['X-Requested-With'] = 'XMLHttpRequest';
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
        headers['Expires'] = '0';

        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        return headers;
    }

    async makeRequest(url, options = {}) {
        if (!this.isOnline) {
            throw new Error('Нет подключения к интернету. Проверьте соединение и попробуйте снова.');
        }

        const timeout = options.timeout || 15000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            let data;
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                data = { success: false, message: 'Сервер вернул некорректные данные' };
            }

            return {
                ok: response.ok,
                status: response.status,
                data: data
            };
        } catch (error) {
            clearTimeout(timeoutId);
            throw this.handleError(error);
        }
    }

    handleError(error) {
        if (error.name === 'AbortError') {
            return new Error('Превышено время ожидания. Попробуйте еще раз.');
        } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
            return new Error('Ошибка сети. Проверьте интернет-соединение.');
        } else if (error.message.includes('Load failed')) {
            return new Error('Ошибка загрузки данных. Попробуйте перезагрузить страницу.');
        } else if (error.message.includes('JSON')) {
            return new Error('Сервер вернул некорректные данные. Обратитесь в поддержку.');
        } else if (error.message.includes('интернет')) {
            return error;
        } else {
            return new Error(`Ошибка соединения: ${error.message}`);
        }
    }

    isAuthenticated() {
        return !!this.token;
    }

    // ВХОД
    async login(credentials) {
        try {
            console.log('Starting login process...');

            const result = await this.makeRequest(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(credentials),
            });

            if (result.ok && result.data.success) {
                this.token = result.data.data.token || result.data.data.access_token;
                if (this.token) {
                    localStorage.setItem('auth_token', this.token);
                    console.log('Login successful');
                }
                return { success: true, data: result.data.data };
            }

            return {
                success: false,
                message: result.data.message || 'Ошибка входа',
                errors: result.data.errors || null
            };
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // РЕГИСТРАЦИЯ
    async register(userData) {
        try {
            console.log('Starting registration process...');

            const result = await this.makeRequest(`${API_BASE_URL}/auth/register`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(userData),
            });

            if (result.ok && result.data.success) {
                this.token = result.data.data.token || result.data.data.access_token;
                if (this.token) {
                    localStorage.setItem('auth_token', this.token);
                    console.log('Registration successful');
                }
                return { success: true, data: result.data.data };
            }

            return {
                success: false,
                message: result.data.message || 'Ошибка регистрации',
                errors: result.data.errors || null
            };
        } catch (error) {
            console.error('Registration error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ================================
    // ВОССТАНОВЛЕНИЕ ПАРОЛЯ (под существующий backend API)
    // ================================

    /**
     * ШАГ 1: Запрос на восстановление пароля
     * Использует эндпоинт /auth/password-recovery из AuthController
     * @param {string} identifier - email или login пользователя
     * @param {string} recoveryMethod - 'email' | 'telegram' | 'both'
     */
    async requestPasswordRecovery(identifier, recoveryMethod = 'email') {
        try {
            console.log('Requesting password recovery for:', identifier);

            // Явно приводим к строке и убираем пробелы
            const cleanIdentifier = String(identifier).trim();

            console.log('Clean identifier:', cleanIdentifier);
            console.log('Recovery method:', recoveryMethod);

            const result = await this.makeRequest(`${API_BASE_URL}/auth/password-recovery`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    identifier: cleanIdentifier,
                    recoveryMethod: String(recoveryMethod)
                }),
            });

            console.log('Response from server:', result);

            if (result.ok && result.data.success) {
                return {
                    success: true,
                    message: result.data.message || 'Инструкции отправлены'
                };
            }

            return {
                success: false,
                message: result.data.message || 'Ошибка запроса',
                errors: result.data.errors || null
            };
        } catch (error) {
            console.error('Request password recovery error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    /**
     * ШАГ 2: Проверка токена восстановления
     * Использует эндпоинт /auth/verify-reset-token из AuthController
     * @param {string} email - Email пользователя
     * @param {string} token - Токен из ссылки
     */
    async verifyResetToken(email, token) {
        try {
            console.log('Verifying reset token...');

            const result = await this.makeRequest(`${API_BASE_URL}/auth/verify-reset-token`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    email: email,
                    token: token
                }),
            });

            return {
                success: result.ok && result.data.success,
                message: result.data.message || (result.ok ? 'Токен действителен' : 'Неверный токен')
            };
        } catch (error) {
            console.error('Verify reset token error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    /**
     * ШАГ 3: Установка нового пароля
     * Использует эндпоинт /auth/reset-password из AuthController
     * @param {string} token - Токен восстановления
     * @param {string} email - Email пользователя
     * @param {string} password - Новый пароль
     * @param {string} password_confirmation - Подтверждение пароля
     */
    async resetPasswordWithToken(token, email, password, password_confirmation) {
        try {
            console.log('Resetting password...');

            const result = await this.makeRequest(`${API_BASE_URL}/auth/reset-password`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    token: token,
                    email: email,
                    password: password,
                    password_confirmation: password_confirmation
                }),
            });

            return {
                success: result.ok && result.data.success,
                message: result.data.message || (result.ok ? 'Пароль успешно изменен' : 'Ошибка изменения пароля')
            };
        } catch (error) {
            console.error('Reset password error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ================================
    // КОНЕЦ МЕТОДОВ ВОССТАНОВЛЕНИЯ ПАРОЛЯ
    // ================================

    // ПОЛУЧЕНИЕ ТЕКУЩЕГО ПОЛЬЗОВАТЕЛЯ
    async getCurrentUser() {
        try {
            if (!this.token) {
                return { success: false, message: 'Токен не найден' };
            }

            const result = await this.makeRequest(`${API_BASE_URL}/auth/user`, {
                method: 'GET',
                headers: this.getHeaders(),
            });

            if (result.ok && result.data.success) {
                return { success: true, data: result.data.data };
            }

            if (result.status === 401) {
                this.logout();
                return { success: false, message: 'Сессия истекла' };
            }

            return {
                success: false,
                message: result.data.message || 'Ошибка получения данных'
            };
        } catch (error) {
            console.error('Get current user error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ВЫХОД
    logout() {
        this.token = null;
        localStorage.removeItem('auth_token');
        try {
            localStorage.removeItem('user_data');
        } catch (e) {
            console.warn('Could not clear additional data:', e);
        }
    }

    // ОБНОВЛЕНИЕ ПРОФИЛЯ
    async updateProfile(profileData) {
        try {
            const result = await this.makeRequest(`${API_BASE_URL}/auth/profile`, {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify(profileData),
            });

            return {
                success: result.ok && result.data.success,
                data: result.data.data,
                message: result.data.message || (result.ok ? 'Профиль обновлен' : 'Ошибка обновления')
            };
        } catch (error) {
            console.error('Update profile error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    async changePassword(passwordData) {
        try {
            const result = await this.makeRequest(`${API_BASE_URL}/profile/password`, {
                method: 'POST', // или PUT, в зависимости от того, что в api.php
                headers: this.getHeaders(),
                body: JSON.stringify(passwordData),
            });

            return {
                success: result.ok && result.data.success,
                message: result.data.message || (result.ok ? 'Пароль изменён' : 'Ошибка изменения пароля')
            };
        } catch (error) {
            console.error('Change password error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ЗАГРУЗКА АВАТАРА
    async uploadAvatar(file) {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const result = await this.makeRequest(`${API_BASE_URL}/auth/avatar`, {
                method: 'POST',
                headers: this.getHeaders(true),
                body: formData,
            });

            return {
                success: result.ok && result.data.success,
                data: result.data.data,
                message: result.data.message || (result.ok ? 'Аватар загружен' : 'Ошибка загрузки')
            };
        } catch (error) {
            console.error('Upload avatar error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }
    // ПРОВЕРКА РЕФЕРАЛЬНОГО КОДА
    async checkReferralCode(referralCode) {
        try {
            console.log('Checking referral code:', referralCode);

            if (!referralCode || typeof referralCode !== 'string' || !referralCode.trim()) {
                return { success: false, message: 'Некорректный реферальный код' };
            }

            const trimmedCode = referralCode.trim();
            const requestData = { referral_code: trimmedCode };

            const result = await this.makeRequest(`${API_BASE_URL}/auth/check-referral`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(requestData),
            });

            if (result.ok && result.data.success) {
                console.log('Referral check successful:', result.data.data);

                // Возвращаем ВСЕ поля от API
                return {
                    success: true,
                    data: {
                        id: result.data.data.id,
                        login: result.data.data.login,
                        email: result.data.data.email,
                        first_name: result.data.data.first_name,
                        last_name: result.data.data.last_name,
                        full_name: result.data.data.full_name,
                        level: result.data.data.level,
                        avatar: result.data.data.avatar
                    }
                };
            }

            return {
                success: false,
                message: result.data.message || 'Недействительный реферальный код'
            };
        } catch (error) {
            console.error('Referral check error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }
    // ПРОВЕРКА ДОСТУПНОСТИ ЛОГИНА
    async checkLoginAvailability(login) {
        try {
            const result = await this.makeRequest(`${API_BASE_URL}/auth/check-login`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ login }),
            });

            return {
                success: result.ok,
                available: result.data.available,
                message: result.data.message
            };
        } catch (error) {
            console.error('Check login error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ПРОВЕРКА ДОСТУПНОСТИ EMAIL
    async checkEmailAvailability(email) {
        try {
            const result = await this.makeRequest(`${API_BASE_URL}/auth/check-email`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ email }),
            });

            return {
                success: result.ok,
                available: result.data.available,
                message: result.data.message
            };
        } catch (error) {
            console.error('Check email error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // ТЕСТ СОЕДИНЕНИЯ
    async testConnection() {
        try {
            const result = await this.makeRequest(`${API_BASE_URL}/health`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' },
            });

            return {
                success: result.ok,
                message: result.ok ? 'Соединение работает' : 'Проблемы с соединением',
                data: result.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.message
            };
        }
    }

    // СТАТИСТИКА КОМАНДЫ
    async getTeamStats() {
        try {
            if (!this.token) {
                return { success: false, message: 'Токен не найден' };
            }

            console.log('Fetching team stats...');

            const result = await this.makeRequest(`${API_BASE_URL}/auth/team-stats`, {
                method: 'GET',
                headers: this.getHeaders(),
            });

            if (result.ok && result.data.success) {
                console.log('Team stats loaded successfully');
                return { success: true, data: result.data.data };
            }

            if (result.status === 401) {
                this.logout();
                return { success: false, message: 'Сессия истекла' };
            }

            return {
                success: false,
                message: result.data.message || 'Ошибка получения статистики'
            };
        } catch (error) {
            console.error('Get team stats error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }
}

export const authService = new AuthService();
